<template>
  <div
    class="colour-swatches"
    :class="{
      [$class('loading')]: state === 'loading',
      'colour-swatches--large': large,
    }"
  >
    <div
      v-if="labels"
      class="colour-swatches__header"
    >
      <p
        class="colour-swatches__option text-body-xs text-body-s-desktop"
        v-text="$string('product.form.colour')"
      />

      <p
        class="colour-swatches__colour text-body-xs text-body-s-desktop"
        v-text="quickviewLoading ? $string('accessibility.loading') : activeColour?.label"
      />
    </div>

    <div class="colour-swatches__swatches">
      <component
        :is="links ? 'a' : 'button'"
        v-for="(colour, index) of limitedColours"
        :key="`${colour.colour}-${index}`"
        class="colour-swatches__swatch-container"
        :class="{ [$class('active')]: activeColour?.handle === colour.handle }"
        :href="links ? getHref(colour) : null"
        :tabindex="visible ? null : '-1'"
        :title="$string('product.swatches.view', { replace: { colour: colour.label } })"
        :type="links ? null : 'button'"
        @click="links ? null : handleClick(colour)"
      >
        <span
          class="colour-swatches__swatch"
          :class="{ 'colour-swatches__swatch--border': isWhiteSwatch(colour) }"
          :style="getStyles(colour)"
          v-text="$string('product.swatches.view', { replace: { colour: colour.label } })"
        />
      </component>

      <div
        v-if="limited && state === 'ready' && count > 0"
        class="colour-swatches__more text-body-2xs text-label-s-desktop"
        v-text="$string('product.swatches.plus', { replace: { count } })"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Colour swatches (colour-swatches)
 * -----------------------------------------------------------------------------
 * Linked product colour swatches used to change product.
 *
 * @param {String} [animationDelay] - Animation delay style.
 * @param {Object} colours - Linked colour handles.
 * @param {String} handle - Current active handle.
 * @param {Boolean} [isQuickview] - If being used by a QuickView component.
 * @param {Boolean} [labels] - Show labels.
 * @param {Boolean} [large] - Use large layout.
 * @param {Boolean} [limited] - Limited number of colour swatches displayed.
 * @param {Boolean} [links] - Use links instead of radio inputs.
 * @param {Boolean} [visible] - Product card is visible in carousel.
 * @emits updateHandle - Emits handle when new colour swatch is selected.
 *
 */
import { mapGetters } from 'vuex'
import { convertHandleToSentenceCase, convertToHandle } from '~/helpers/convert'

export default {
  name: 'ColourSwatches',

  props: {
    animationDelay: {
      type: String,
      default: '0s',
    },
    colours: {
      type: [Boolean, Object],
      default: () => ({}),
      required: true,
    },
    coloursLoaded: {
      type: Boolean,
      default: false,
    },
    handle: {
      type: String,
      default: '',
      required: true,
    },
    isQuickview: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    limited: {
      type: Boolean,
      default: false,
    },
    links: {
      type: Boolean,
      default: false,
    },
    state: {
      type: String,
      default: 'ready',
    },
    visible: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'update-handle': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      activeColour: '',
      displayColours: [...Array(this.limit)].map(() => ({})),
      formattedColours: [],
      limit: cnvs.settings.colourSwatchesCount,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute count of excess products (when limited).
     * @returns {Number}
     */
    count() {
      return this.formattedColours?.length - this.limit
    },

    /**
     * Compute limited formatted colours.
     * @returns {Array{}}
     */
    limitedColours() {
      return this.limited
        ? this.displayColours?.slice(0, this.limit)
        : this.displayColours
    },

    /**
     * Compute if in quick view loading state.
     * @returns {Boolean}
     */
    quickviewLoading() {
      return this.isQuickview && !this.handle
    },
  },

  watch: {

    /**
     * Watch colours to update display colours.
     */
    coloursLoaded() {
      if (!this.coloursLoaded) {
        return
      }

      this.setFormattedColours()
    },

    /**
     * Watch state to update display colours.
     */
    state() {
      if (this.state === 'loading') {
        this.displayColours = [...Array(this.limit)].map(() => ({}))
        return
      }

      this.displayColours = this.formattedColours
    },
  },

  mounted() {
    this.setFormattedColours()
    this.activeColour = this.formattedColours?.find((colour) => colour.active)
  },

  methods: {

    /**
     * Pulls in stored colour data to maintain colour swatch order, even after
     * product data has been queried (i.e. in the quickview component).
     * - The active colour is updated based on the handle prop.
     * - Only runs if stored colour data exists in session storage.
     */
    useStoredColours() {
      const storedColours = JSON.parse(sessionStorage.getItem('cnvs-quickview-colour-order'))

      storedColours.find((colour) => colour.active === true).active = false
      storedColours.find(colour => colour.handle === this.handle).active = true

      this.formattedColours = storedColours
      this.displayColours = this.formattedColours
    },

    /**
     * Set formatted colours.
     * - Set on mounted instead of computing so it doesn't update when switching
     *   colours so initial active colour remains first.
     * - Formatted colours are separate from display colours so we can update
     *   the loading state without losing initial active colour position.
     */
    setFormattedColours() {

      /**
       * If the quickview modal is open, pull in stored colour data. If no
       * stored colour data exists, then format `colours` and create
       * an item in session storage.
       */
      if (
        this.getActiveOverlay === 'quickView' &&
        JSON.parse(sessionStorage.getItem(`cnvs-quickview-colour-order`))
      ) {
        this.useStoredColours()
        return
      }

      const colours = this.quickviewLoading
        ? ['loading-1', 'loading-2', 'loading-3']
        : this.colours

      this.formattedColours = Object.entries(colours)
        .map(([colour, handle]) => {
          const sanitisedColour = convertToHandle(colour)
          const sentenceLabel = convertHandleToSentenceCase(sanitisedColour)
          const label = `${sentenceLabel.slice(0, 1).toUpperCase()}${sentenceLabel.slice(1)}`

          return {
            active: this.handle === handle,
            colour: sanitisedColour,
            handle,
            label,
            url: cnvs.colours[sanitisedColour] ?? '',
          }
        })
        .sort((a, b) => {
          if (a.active) {
            return -1
          }

          if (b.active) {
            return 1
          }

          return 0
        })

      this.displayColours = this.formattedColours

      if (this.quickviewLoading) {
        return
      }

      sessionStorage.setItem(
        'cnvs-quickview-colour-order',
        JSON.stringify(this.displayColours),
      )
    },

    /**
     * Get href attribute for links.
     * @param {Object} colour - Current colour object.
     * @returns {String|Null}
     */
    getHref(colour) {
      return cnvs.page.collection
        ? `//${location.host}${cnvs.routes.normalisedRoot}collections/${cnvs.page.collection}/products/${colour.handle}`
        : `//${location.host}${cnvs.routes.normalisedRoot}products/${colour.handle}`
    },

    /**
     * Get styles object.
     * @param {Object} colour - Current colour object.
     * @param {Number} index - Colour index.
     * @returns {Object}
     */
    getStyles(colour) {
      if (this.state === 'loading') {
        return {
          animationDelay: this.animationDelay,
        }
      }

      const styles = {
        backgroundColor: colour.colour,
      }

      if (colour.url) {
        styles.backgroundImage = `url(${colour.url})`
      }

      return styles
    },

    /**
     * Get if colour is a white swatch.
     * - Included in white swatch setting.
     * @param {Object} colour - Current colour object.
     * @returns {Boolean}
     */
    isWhiteSwatch(colour) {
      return cnvs.settings.whiteSwatches?.find((handle) => {
        return colour.colour === handle.trim()
      })
    },

    /**
     * Handle swatch click.
     * @param {Object} colour - Colour object to load.
     */
    handleClick(colour) {
      if (!colour) {
        return
      }

      this.activeColour = colour
      this.$emit('update-handle', colour.handle)
    },
  },
}
</script>

<style lang="scss">
@import './colour-swatches';
</style>
